import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useMotivation } from '../context/MotivationContext';

const promptSuggestions = [
  "I keep making excuses for not working out",
  "I waste too much time on social media",
  "I start projects but never finish them",
  "I know what to do but can't get off my ass",
  "I'm too comfortable being mediocre",
  "I need to stop being lazy and get to work",
  "I talk about my goals but never take action"
];

const MotivationForm = () => {
  const [prompt, setPrompt] = useState('');
  const { getMotivation, isLoading } = useMotivation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (prompt.trim()) {
      await getMotivation(prompt);
      // Don't clear the prompt so user can see what they asked for
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setPrompt(suggestion);
  };

  return (
    <FormContainer>
      <FormTitle>No-BS Motivation</FormTitle>
      <FormDescription>
        Tell me your excuses. I'll tell you what you need to hear, not what you want to hear.
      </FormDescription>

      <Form onSubmit={handleSubmit}>
        <TextArea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="What's your excuse this time? Be honest."
          rows={4}
          required
        />
        <SubmitButton type="submit" disabled={isLoading}>
          {isLoading ? <LoadingSpinner /> : "Get Reality Check"}
        </SubmitButton>
      </Form>

      <SuggestionsContainer>
        <SuggestionsTitle>Try these prompts:</SuggestionsTitle>
        <SuggestionsList>
          {promptSuggestions.map((suggestion, index) => (
            <SuggestionItem
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion}
            </SuggestionItem>
          ))}
        </SuggestionsList>
      </SuggestionsContainer>
    </FormContainer>
  );
};

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const FormContainer = styled.div`
  background: linear-gradient(145deg, #1a1a1a, #0a0a0a);
  border-radius: 8px;
  padding: 2rem;
  margin: 1rem 0;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  max-width: 800px;
  width: 100%;
  border: 1px solid var(--color-secondary);
`;

const FormTitle = styled.h3`
  font-family: var(--font-primary);
  font-size: 1.8rem;
  color: var(--color-gold);
  margin-bottom: 0.5rem;
  text-align: center;
`;

const FormDescription = styled.p`
  color: var(--color-text);
  text-align: center;
  margin-bottom: 1.5rem;
  font-size: 1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TextArea = styled.textarea`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid var(--color-secondary);
  border-radius: 4px;
  padding: 1rem;
  color: var(--color-text);
  font-family: var(--font-secondary);
  font-size: 1rem;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--color-gold);
    box-shadow: 0 0 5px rgba(212, 175, 55, 0.3);
  }

  &::placeholder {
    color: var(--color-secondary);
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(145deg, #1a1a1a, #0a0a0a);
  border: 1px solid var(--color-gold);
  border-radius: 30px;
  padding: 0.8rem 1.5rem;
  color: var(--color-gold);
  font-family: var(--font-secondary);
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover:not(:disabled) {
    background: var(--color-gold);
    color: #000;
    box-shadow: 0 5px 15px rgba(212, 175, 55, 0.3);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(212, 175, 55, 0.3);
  border-top: 2px solid var(--color-gold);
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const SuggestionsContainer = styled.div`
  margin-top: 2rem;
`;

const SuggestionsTitle = styled.h4`
  font-family: var(--font-primary);
  color: var(--color-text);
  margin-bottom: 0.8rem;
  font-size: 1.1rem;
`;

const SuggestionsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const SuggestionItem = styled.button`
  background: transparent;
  border: 1px solid var(--color-secondary);
  border-radius: 20px;
  padding: 0.5rem 1rem;
  color: var(--color-secondary);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--color-gold);
    color: var(--color-gold);
    background: rgba(212, 175, 55, 0.1);
  }
`;

export default MotivationForm;
